import { useEffect, useState, type ReactNode } from "react";
import { Box, Stack, Typography, Button, Container, IconButton, Drawer, List, ListItemButton, ListItemText } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import athrivIcon from "@/assets/athriv-icon.png";
import { trackPageView, trackCtaClick } from "@/utils/analytics";

const NAV_LINKS = [
  { label: "Features", href: "/features" },
  { label: "Pricing", href: "/pricing" },
  { label: "Security", href: "/security" },
  { label: "Resources", href: "/resources" }
];

const FOOTER_PRODUCT_LINKS = [
  { label: "Features", href: "/features" },
  { label: "Pricing", href: "/pricing" },
  { label: "Security", href: "/security" }
];

const FOOTER_COMPANY_LINKS = [
  { label: "About", href: "/about" },
  { label: "Contact", href: "/contact" },
  { label: "Blog", href: "/blog" }
];

const FOOTER_LEGAL_LINKS = [
  { label: "Terms of Service", href: "/terms" },
  { label: "Privacy Policy", href: "/privacy" }
];

/** Shared header + footer chrome for every public marketing page (homepage,
 * /features/*, /pricing, /security, /about, /contact, /resources, /blog/*).
 * Kept separate from AppLayout (the authenticated app's sidebar shell) since
 * these two audiences - anonymous visitors vs. signed-in users - never
 * overlap and have completely different navigation needs. */
export default function MarketingLayout({ children }: { children: ReactNode }) {
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    // Deferred a tick so a page's own useDocumentTitle (set in the child
    // page component, which mounts and runs its effect before this parent
    // layout's effect does) has already updated document.title - avoids
    // reporting the previous page's stale title on the very first pageview.
    const id = window.setTimeout(() => trackPageView(window.location.pathname, document.title), 0);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.pathname]);

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Box component="header" sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
        <Container maxWidth="xl">
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 2 }}>
            <Stack component="a" href="/" direction="row" spacing={1.5} alignItems="center" sx={{ textDecoration: "none", color: "inherit" }}>
              <Box component="img" src={athrivIcon} alt="Athriv Technologies" sx={{ width: 30, height: 30, objectFit: "contain" }} />
              <Typography variant="h6" fontWeight={800} sx={{ fontFamily: '"Manrope", sans-serif' }}>
                AssetOps <Box component="span" sx={{ color: "text.secondary", fontWeight: 500 }}>by Athriv</Box>
              </Typography>
            </Stack>

            <Stack direction="row" spacing={4} alignItems="center" sx={{ display: { xs: "none", md: "flex" } }}>
              {NAV_LINKS.map((link) => (
                <Typography key={link.href} component="a" href={link.href} variant="body2" fontWeight={600}
                  sx={{ textDecoration: "none", color: "text.primary", "&:hover": { color: "primary.main" } }}>
                  {link.label}
                </Typography>
              ))}
            </Stack>

            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ display: { xs: "none", md: "flex" } }}>
              <Button variant="text" href="/login" onClick={() => trackCtaClick("sign_in_nav")}>Sign In</Button>
              <Button variant="contained" href="/login?signup=1" onClick={() => trackCtaClick("start_free_nav")}>Start Free</Button>
            </Stack>

            <IconButton sx={{ display: { xs: "flex", md: "none" } }} onClick={() => setMobileOpen(true)} aria-label="Open menu">
              <MenuIcon />
            </IconButton>
          </Stack>
        </Container>
      </Box>

      <Drawer anchor="right" open={mobileOpen} onClose={() => setMobileOpen(false)}>
        <Box sx={{ width: 260, pt: 1 }}>
          <Stack direction="row" justifyContent="flex-end" sx={{ px: 1 }}>
            <IconButton onClick={() => setMobileOpen(false)} aria-label="Close menu"><CloseIcon /></IconButton>
          </Stack>
          <List>
            {NAV_LINKS.map((link) => (
              <ListItemButton key={link.href} component="a" href={link.href}>
                <ListItemText primary={link.label} />
              </ListItemButton>
            ))}
            <ListItemButton component="a" href="/login">
              <ListItemText primary="Sign In" />
            </ListItemButton>
            <ListItemButton component="a" href="/login?signup=1">
              <ListItemText primary="Start Free" primaryTypographyProps={{ fontWeight: 700, color: "primary.main" }} />
            </ListItemButton>
          </List>
        </Box>
      </Drawer>

      <Box component="main" sx={{ flex: 1 }}>
        {children}
      </Box>

      <Box component="footer" sx={{ borderTop: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}>
        <Container maxWidth="xl" sx={{ py: 6 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={6} justifyContent="space-between">
            <Box sx={{ maxWidth: 280 }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                <Box component="img" src={athrivIcon} alt="Athriv Technologies" sx={{ width: 24, height: 24, objectFit: "contain" }} />
                <Typography variant="subtitle1" fontWeight={800}>AssetOps</Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                IT asset management software for modern businesses, built by Athriv Technologies.
              </Typography>
            </Box>

            <Stack direction={{ xs: "row" }} spacing={{ xs: 4, sm: 8 }} flexWrap="wrap">
              <Stack spacing={1}>
                <Typography variant="subtitle2" fontWeight={700}>Product</Typography>
                {FOOTER_PRODUCT_LINKS.map((link) => (
                  <Typography key={link.href} component="a" href={link.href} variant="body2" color="text.secondary"
                    sx={{ textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
                    {link.label}
                  </Typography>
                ))}
              </Stack>
              <Stack spacing={1}>
                <Typography variant="subtitle2" fontWeight={700}>Company</Typography>
                {FOOTER_COMPANY_LINKS.map((link) => (
                  <Typography key={link.href} component="a" href={link.href} variant="body2" color="text.secondary"
                    sx={{ textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
                    {link.label}
                  </Typography>
                ))}
              </Stack>
              <Stack spacing={1}>
                <Typography variant="subtitle2" fontWeight={700}>Legal</Typography>
                {FOOTER_LEGAL_LINKS.map((link) => (
                  <Typography key={link.href} component="a" href={link.href} variant="body2" color="text.secondary"
                    sx={{ textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
                    {link.label}
                  </Typography>
                ))}
              </Stack>
            </Stack>
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 5, pt: 3, borderTop: "1px solid", borderColor: "divider" }}>
            © {new Date().getFullYear()} Athriv Technologies. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}
